import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';
import { RootState } from '../types';
import { User } from '@/types';
import { connect, getMyUser, disconnect } from '@/services/compte/api';
import { router } from '@/router';

export interface AuthState {
	isAuthenticated: boolean;
	user: User | null;
	loaded: boolean;
	loading: boolean;
}

const authState: AuthState = {
	isAuthenticated: false,
	user: null,
	loaded: false,
	loading: false
};

const actions: ActionTree<AuthState, RootState> = {
	async login({ commit, dispatch }, payload) {
		commit('UPDATE_LOADING', true);

		try {
			await connect(payload);
			commit('UPDATE_IS_AUTHENTICATED', true);
			await dispatch('getMyUser');
		} catch (error) {
			commit('LOGOUT');
			throw error;
		} finally {
			commit('UPDATE_LOADING', false);
		}
	},
	async logout({ commit }, withRedirection = true) {
		let redirectUrl: string | undefined;

		try {
			const response = await disconnect();
			redirectUrl = response.data.redirectUrl;
		} catch {
			// echec serveur : on deconnecte quand meme en local
		} finally {
			commit('LOGOUT');
		}

		if (!withRedirection) {
			return;
		}

		if (redirectUrl) {
			await router.replace(redirectUrl);
		} else {
			// pas d'url de redir : retour accueil
			await router.replace({ name: 'home' }).catch(() => undefined);
		}
	},
	async getMyUser({ commit }) {
		commit('UPDATE_LOADING', true);

		try {
			const user = await getMyUser();

			commit('SET_USER', user.data);
			commit('UPDATE_IS_AUTHENTICATED', true);

			if (!user.data.isCguValid) {
				await router.push({ name: 'updateCgu' });
			}

		} catch {
			throw new Error('');
		} finally {
			commit('UPDATE_LOADING', false);
		}
	}
};

const mutations: MutationTree<AuthState> = {
	UPDATE_LOADED(state, loaded: boolean): void {
		state.loaded = loaded;
	},
	UPDATE_LOADING(state, loading: boolean): void {
		state.loading = loading;
	},
	UPDATE_IS_AUTHENTICATED(state, isAuthenticated: boolean): void {
		state.isAuthenticated = isAuthenticated;
	},
	SET_USER(state, user: User): void {
		state.user = user;
	},
	LOGOUT(state): void {
		state.isAuthenticated = false;
		state.user = null;
		sessionStorage.clear();
	}
};

const getters: GetterTree<AuthState, RootState> = {
	getUser: (state: AuthState): User | null => state.user,
	isAuthenticated: (state: AuthState) => state.isAuthenticated,
	hasUserInfo: (state: AuthState): boolean => state.user !== null,
	getUserType: (state: AuthState): string | null => state.user?.type ?? null,
	isCguValid: (state: AuthState): boolean => state.user?.isCguValid ?? false
};

const auth: Module<AuthState, RootState> = {
	namespaced: true,
	state: authState,
	actions,
	mutations,
	getters
};

export default auth;
